// Tab "Métodos de pagamento" (§6.2) — wave 2 EDITÁVEL + wave 3
// badge de COERÊNCIA: toggles reais (PUT ?active=) com
// otimismo+rollback e AlertDialog no DESATIVAR, remoção (DELETE
// confirmada), seção de catálogo (POST) e o cruzamento com as
// configurações ATIVAS de processador do WL — método habilitado sem
// gateway_configuration ativa ganha badge de aviso ("o instrument
// sairá sem gateway"). Falha da query de processadores = badge some
// silenciosamente (não bloqueia a tab).
//
// A lista de cima é o CADASTRO (white_label_payment_method), nada além:
// o que nunca foi cadastrado vive na seção de baixo, e um clique lá o
// cadastra. Antes as duas coisas vinham misturadas na mesma lista, e
// "nunca cadastrado" ficava indistinguível de "cadastrado e desligado".
import {
  CreditCardIcon,
  PlusIcon,
  SettingsIcon,
  Trash2Icon,
  TriangleAlertIcon,
} from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import { FRONTEND_ROUTES } from "@/constants"
import {
  useAddWhiteLabelPaymentMethodMutation,
  useAdminWhiteLabelPaymentMethods,
  useProcessorConfigs,
  useRemoveWhiteLabelPaymentMethodMutation,
  useUpdateWhiteLabelPaymentMethodMutation,
} from "@/services/admin"
import { usePaymentMethodCatalog } from "@/services/admin/payment-method-catalog"
import { useProcessorConfigMetadata } from "@/services/admin/processor-config"

import { FieldInfo } from "./field-info"

const panelPrefix = "pages.adminWhiteLabels"

// O catálogo do que dá para habilitar vem do backoffice
// (GET /payment-methods → linhas ativas de payment_method). Era uma constante
// aqui, que divergia da lista da tela de merchant e do enum do backend.

// Ação aguardando confirmação no AlertDialog.
type PendingAction =
  | { kind: "deactivate"; method: string }
  | { kind: "remove"; method: string }

type MethodsTabProps = {
  uuid: string
}

export const MethodsTab = ({ uuid }: MethodsTabProps) => {
  const { t } = useTranslation()
  const { data, isLoading, isError, refetch } =
    useAdminWhiteLabelPaymentMethods(uuid)
  const addMutation =
    useAddWhiteLabelPaymentMethodMutation(uuid)
  const updateMutation =
    useUpdateWhiteLabelPaymentMethodMutation(uuid)
  const removeMutation =
    useRemoveWhiteLabelPaymentMethodMutation(uuid)

  const [pendingAction, setPendingAction] =
    useState<PendingAction | null>(null)

  // O catálogo deixou de ser acessório: com a lista mostrando só o
  // cadastrado, ele é a ÚNICA forma de cadastrar o primeiro método. Se
  // falhar, a aba de um WL novo ficaria sem nenhuma ação possível — por
  // isso a seção tem estado próprio de carregando e de erro com retry.
  const {
    data: catalog,
    isLoading: isCatalogLoading,
    isError: isCatalogError,
    refetch: refetchCatalog,
  } = usePaymentMethodCatalog()

  // Coerência (wave 3): configs ATIVAS de processador deste WL.
  // Falha aqui NÃO bloqueia a tab — o badge só some.
  const processorConfigsQuery = useProcessorConfigs({
    whiteLabelId: uuid,
    active: true,
  })
  // Rótulo humano de cada processador — o mesmo do painel de credenciais.
  const { data: processorMetadata } =
    useProcessorConfigMetadata()
  const gatewayLabels = new Map(
    (processorMetadata ?? []).map((item) => [
      item.gateway,
      item.label,
    ])
  )

  // Qual configuração ATIVA atende cada método — é ela que o link abre.
  const configByMethod = new Map(
    (processorConfigsQuery.data?.content ?? []).map(
      (config) => [config.method as string, config]
    )
  )
  const methodsWithProcessor = new Set(
    configByMethod.keys()
  )

  // Leva à configuração do método NESTE white label. Com configuração, abre a
  // que já existe; sem ela, abre o stepper com white label e método já
  // escolhidos — as duas perguntas que esta tela já respondeu.
  const configureHref = (method: string) => {
    const config = configByMethod.get(method)
    const params = new URLSearchParams({ whiteLabel: uuid })
    if (config) {
      params.set("config", config.id)
    } else {
      params.set("method", method)
    }
    return `${FRONTEND_ROUTES.ADMIN_PROCESSOR_CONFIG.href}?${params}`
  }

  // Nome da credencial que atende o método; sem metadata do gateway, o próprio
  // nome do banco já distingue mais do que o id da configuração.
  const processorName = (method: string) => {
    const config = configByMethod.get(method)
    if (!config)
      return t(`${panelPrefix}.paymentMethods.noProcessor`)
    return t(`${panelPrefix}.paymentMethods.processedBy`, {
      // Rótulo humano do metadata; sem ele, o enum cru (BANCO_DO_BRASIL).
      gateway:
        gatewayLabels.get(config.gateway) ?? config.gateway,
    })
  }

  // Aviso só com o cruzamento DISPONÍVEL: método habilitado (toggle
  // on) sem config ativa de processador.
  const showCoherenceWarning = (method: {
    method: string
    active: boolean
  }): boolean =>
    processorConfigsQuery.isSuccess &&
    method.active &&
    !methodsWithProcessor.has(method.method)

  // Rótulo traduzido; sem tradução, cai na descrição vinda do catálogo (o
  // backoffice já manda algo legível) e só então no nome do enum.
  const methodLabel = (method: string) =>
    t(`${panelPrefix}.paymentMethods.names.${method}`, {
      defaultValue:
        catalog?.find(
          (item) => item.paymentMethod === method
        )?.description ?? method,
    })

  if (isLoading) {
    return (
      <div
        data-testid="wl-methods-skeleton"
        className="space-y-3"
      >
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton
            key={`wl-methods-skeleton-${index}`}
            className="h-16 w-full rounded-xl"
          />
        ))}
      </div>
    )
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-6">
        <p className="text-center text-sm text-red-700">
          {t(`${panelPrefix}.paymentMethods.loadError`)}
        </p>
        <Button
          type="button"
          variant="outline"
          onClick={() => refetch()}
        >
          {t(`${panelPrefix}.retry`)}
        </Button>
      </div>
    )
  }

  // `Array.isArray` e não `?? []`: o 204 de lista vazia do gateway chega
  // como STRING vazia, que passa pelo nullish e estoura no `.map` abaixo.
  const methods = Array.isArray(data) ? data : []
  const configuredMethods = new Set(
    methods.map((item) => item.method)
  )
  // O que ainda não foi cadastrado — vira a lista de cadastro rápido.
  const availableMethods = (catalog ?? [])
    .map((item) => item.paymentMethod)
    .filter((method) => !configuredMethods.has(method))

  const isMutating =
    addMutation.isPending ||
    updateMutation.isPending ||
    removeMutation.isPending

  // Ativar dispara direto (otimista); desativar SEMPRE confirma.
  const handleToggle = (
    method: string,
    nextActive: boolean
  ) => {
    if (!nextActive) {
      setPendingAction({ kind: "deactivate", method })
      return
    }
    updateMutation.mutate({ method, active: true })
  }

  const handleConfirmPending = () => {
    if (pendingAction?.kind === "deactivate") {
      updateMutation.mutate({
        method: pendingAction.method,
        active: false,
      })
    }
    if (pendingAction?.kind === "remove") {
      removeMutation.mutate(pendingAction.method)
    }
    setPendingAction(null)
  }

  return (
    <div className="space-y-4">
      {/* Cabeçalho da seção com a explicação do que é habilitar um
          método e do que significa o badge de coerência. */}
      <div className="flex items-center gap-1.5">
        <h2 className="text-sm font-semibold">
          {t(`${panelPrefix}.paymentMethods.sectionTitle`)}
        </h2>
        <FieldInfo
          hintKey={`${panelPrefix}.paymentMethods.descriptions.section`}
        />
      </div>

      {/* Sem cadastro não há card de vazio: a aba cai direto no catálogo
          abaixo, que é a única coisa que se pode fazer aqui. */}
      {methods.length > 0 && (
        <ul className="flex flex-col gap-3">
          {methods.map((method) => (
            <li
              key={method.method}
              data-testid={`wl-method-${method.method}`}
              className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-card p-4 shadow-sm"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-muted">
                  {method.thumbUrl ? (
                    <img
                      src={method.thumbUrl}
                      alt=""
                      aria-hidden="true"
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <CreditCardIcon
                      className="size-5 text-muted-foreground"
                      aria-hidden="true"
                    />
                  )}
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="flex flex-wrap items-center gap-2 text-sm font-semibold">
                    {methodLabel(method.method)}
                  </span>
                  {/* Quem processa é a informação que faltava aqui: o aviso
                      de "sem processador" dizia só metade, e ficava ao lado de
                      um botão que dizia a outra metade. */}
                  {/* Método desligado sem processador não é incoerência, é
                      escolha: afirmar "sem processador" ali seria alarme falso. */}
                  {processorConfigsQuery.isSuccess &&
                    (method.active ||
                      configByMethod.has(
                        method.method
                      )) && (
                      <span
                        data-testid={`method-processor-${method.method}`}
                        className={
                          showCoherenceWarning(method)
                            ? "flex items-center gap-1 text-xs font-medium text-amber-700"
                            : "text-xs text-muted-foreground"
                        }
                      >
                        {showCoherenceWarning(method) ? (
                          <>
                            <TriangleAlertIcon
                              className="size-3 shrink-0"
                              aria-hidden="true"
                            />
                            {t(
                              `${panelPrefix}.paymentMethods.noProcessor`
                            )}
                          </>
                        ) : (
                          processorName(method.method)
                        )}
                      </span>
                    )}
                  {method.description && (
                    <span className="text-xs text-muted-foreground">
                      {method.description}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Só depois que o cruzamento chega: oferecer "abrir a
                    configuração" antes de saber se ela existe levaria o
                    operador ao stepper de uma que já está criada. */}
                {processorConfigsQuery.isSuccess && (
                  <Button
                    asChild
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid={`method-configure-${method.method}`}
                  >
                    <Link to={configureHref(method.method)}>
                      <SettingsIcon
                        className="mr-1.5 size-3.5"
                        aria-hidden="true"
                      />
                      {configByMethod.has(method.method)
                        ? t(
                            `${panelPrefix}.paymentMethods.openProcessor`
                          )
                        : t(
                            `${panelPrefix}.paymentMethods.setProcessor`
                          )}
                    </Link>
                  </Button>
                )}
                <Switch
                  checked={method.active}
                  disabled={isMutating}
                  aria-label={t(
                    `${panelPrefix}.paymentMethods.toggleAria`,
                    {
                      method: methodLabel(method.method),
                    }
                  )}
                  onCheckedChange={(nextActive) =>
                    handleToggle(method.method, nextActive)
                  }
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground hover:text-destructive"
                  disabled={isMutating}
                  aria-label={t(
                    `${panelPrefix}.paymentMethods.removeAria`,
                    {
                      method: methodLabel(method.method),
                    }
                  )}
                  onClick={() =>
                    setPendingAction({
                      kind: "remove",
                      method: method.method,
                    })
                  }
                >
                  <Trash2Icon
                    className="size-4"
                    aria-hidden="true"
                  />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Catálogo do que ainda não foi cadastrado — clicar cadastra
          (POST). É a única porta de entrada de um método novo, então
          carrega e falha por conta própria, sem derrubar a lista. */}
      <section
        data-testid="wl-methods-catalog"
        className="space-y-2"
      >
        <h3 className="text-sm font-semibold text-muted-foreground">
          {t(`${panelPrefix}.paymentMethods.addTitle`)}
        </h3>

        {isCatalogLoading && (
          <div className="flex flex-wrap gap-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton
                key={`wl-methods-catalog-skeleton-${index}`}
                className="h-8 w-28 rounded-md"
              />
            ))}
          </div>
        )}

        {isCatalogError && (
          <div className="flex flex-col items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
            <p className="text-center text-sm text-red-700">
              {t(
                `${panelPrefix}.paymentMethods.catalogLoadError`
              )}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => refetchCatalog()}
            >
              {t(`${panelPrefix}.retry`)}
            </Button>
          </div>
        )}

        {!isCatalogLoading &&
          !isCatalogError &&
          availableMethods.length === 0 && (
            <p className="text-xs text-muted-foreground">
              {t(
                `${panelPrefix}.paymentMethods.allMethodsRegistered`
              )}
            </p>
          )}

        {availableMethods.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {availableMethods.map((method) => (
              <Button
                key={method}
                type="button"
                variant="outline"
                size="sm"
                disabled={isMutating}
                onClick={() => addMutation.mutate(method)}
              >
                <PlusIcon
                  className="mr-1 size-3.5"
                  aria-hidden="true"
                />
                {methodLabel(method)}
              </Button>
            ))}
          </div>
        )}
      </section>

      {/* Confirmação de desativar/remover — nunca silenciosa. */}
      <AlertDialog
        open={pendingAction !== null}
        onOpenChange={(open) => {
          if (!open) setPendingAction(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingAction?.kind === "remove"
                ? t(
                    `${panelPrefix}.paymentMethods.removeDialog.title`
                  )
                : t(
                    `${panelPrefix}.paymentMethods.deactivateDialog.title`
                  )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingAction?.kind === "remove"
                ? t(
                    `${panelPrefix}.paymentMethods.removeDialog.description`,
                    {
                      method: methodLabel(
                        pendingAction.method
                      ),
                    }
                  )
                : t(
                    `${panelPrefix}.paymentMethods.deactivateDialog.description`,
                    {
                      method: methodLabel(
                        pendingAction?.method ?? ""
                      ),
                    }
                  )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t(
                `${panelPrefix}.paymentMethods.dialogCancel`
              )}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmPending}
            >
              {pendingAction?.kind === "remove"
                ? t(
                    `${panelPrefix}.paymentMethods.removeDialog.confirm`
                  )
                : t(
                    `${panelPrefix}.paymentMethods.deactivateDialog.confirm`
                  )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
